import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Container,
  Group,
  Stack,
  Text,
  Image,
  useMantineTheme,
  useMantineColorScheme,
  SimpleGrid,
} from "@mantine/core";
import {
  ShieldCheckIcon,
  HandHeartIcon,
  PulseIcon,
  CaretRightIcon,
  CaretLeftIcon,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { useTermsAccepted, useUpdateMe, useMe, useCheckHealthData } from "@/api";
import TermsConditionsModal from "./terms-conditions-modal";

// Data Fetched Screen Component
const DataFetchedScreen: React.FC<{setIsOnboarding: (isOnboarding: boolean) => void}> = ({setIsOnboarding}) => {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const [userFirstName, setUserFirstName] = useState("");
  const [activeDot, setActiveDot] = useState(1); 

  useEffect(() => {
    // Get user data from localStorage
    const storedUser = localStorage.getItem("tanstack.auth.user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserFirstName(userData?.user?.first_name || "");
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleDotClick = (dotNumber: number) => {
    setActiveDot(dotNumber);
  };

  const handlePrevious = () => {
    setActiveDot(prev => prev > 1 ? prev - 1 : 3);
  };

  const handleNext = () => {
    setActiveDot(prev => prev < 3 ? prev + 1 : 1);
  };

  const handleContinue = () => {
    console.log("Continue button clicked!");
    setIsOnboarding(true);
  };

  return (
    <Box 
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[0],
        padding: "20px",
      }}
    >
      <Stack align="center" gap="xl">
        {/* Logo */}
        <Image
          src={
            colorScheme === "dark"
              ? "/assets/white_logo.png"
              : "/assets/logo_main.png"
          }
          w={180}
          alt="OneSelf Logo"
        />

        <Card 
          shadow="sm" 
          padding="xl" 
          radius="md" 
          withBorder
          style={{
            width: "500px", 
            minHeight: "413px", 
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Stack align="center" gap="md" style={{ flex: 1, justifyContent: "center" }}>
            {/* Content based on active dot */}
            {activeDot === 1 && (
              <Box style={{ position: "relative", width: "100%" }}>
                <Text 
                  size="xl"
                  fw={600} 
                  c={colorScheme === "dark" ? "white" : "dark.7"}
                  mb="md"
                  ta="center"
                >
                  Hello {userFirstName},
                </Text> 
              <Box
                style={{
                  position: "relative",
                  width: "100%",
                  height: "200px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text 
                  size="md"
                  c={colorScheme === "dark" ? "gray.4" : "dark.5"}
                  ta="center"
                  lh={1.6}
                  style={{ padding: "0 50px" }}
                >
                  We are excited you have chosen to join and begin your Path to better health. We are Getting your information ready. This should not take long.
                </Text>
              </Box>

              {/* Left Arrow - CaretLeftIcon */}
              <Button
                variant="subtle"
                size="sm"
                radius="xl"
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "60%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#9CA3AF",
                  border: "none",
                  color: "#FFFFFF",
                  width: "26px",
                  height: "26px",
                  minWidth: "26px",
                  padding: 0,
                  zIndex: 10,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handlePrevious}
              >
                <CaretLeftIcon size={12} />
              </Button>

              {/* Right Arrow - CaretRightIcon */}
              <Button
                variant="subtle"
                size="sm"
                radius="xl"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "60%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#9CA3AF",
                  border: "none",
                  color: "#FFFFFF",
                  width: "26px",
                  height: "26px",
                  minWidth: "26px",
                  padding: 0,
                  zIndex: 10,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handleNext}
              >
                <CaretRightIcon size={12} />
              </Button>
            </Box>
          )}
          
          {activeDot === 2 && (
            <Box style={{ position: "relative", width: "100%" }}>
              <Text 
                size="xl"
                fw={600} 
                c={colorScheme === "dark" ? "white" : "dark.7"}
                mb="md"
                ta="center"
              >
                Hello {userFirstName},
              </Text>
          
              
              <Image
                src="/assets/carousel-image2.jpg"
                alt="Mountain summit image"
                style={{
                  width: "100%",
                  height: "200px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />

              {/* Left Arrow - CaretLeftIcon */}
              <Button
                variant="subtle"
                size="sm"
                radius="xl"
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "60%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#9CA3AF",
                  border: "none",
                  color: "#FFFFFF",
                  width: "26px",
                  height: "26px",
                  minWidth: "26px",
                  padding: 0,
                  zIndex: 10,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handlePrevious}
              >
                <CaretLeftIcon size={12} />
              </Button>

              {/* Right Arrow - CaretRightIcon */}
              <Button
                variant="subtle"
                size="sm"
                radius="xl"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "60%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#9CA3AF",
                  border: "none",
                  color: "#FFFFFF",
                  width: "26px",
                  height: "26px",
                  minWidth: "26px",
                  padding: 0,
                  zIndex: 10,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handleNext}
              >
                <CaretRightIcon size={12} />
              </Button>
            </Box>
          )}
          
          {activeDot === 3 && (
            <Box style={{ position: "relative", width: "100%" }}>
              <Text 
                size="xl"
                fw={600} 
                c={colorScheme === "dark" ? "white" : "dark.7"}
                mb="md"
                ta="center"
              >
                Hello {userFirstName},
              </Text>
              <Image
                src="/assets/carousel-image1.jpg"
                alt="Person walking on path image"
                style={{
                  width: "100%",
                  height: "200px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />

              {/* Left Arrow - CaretLeftIcon */}
              <Button
                variant="subtle"
                size="sm"
                radius="xl"
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "60%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#9CA3AF",
                  border: "none",
                  color: "#FFFFFF",
                  width: "26px",
                  height: "26px",
                  minWidth: "26px",
                  padding: 0,
                  zIndex: 10,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handlePrevious}
              >
                <CaretLeftIcon size={12} />
              </Button>

              {/* Right Arrow - CaretRightIcon */}
              <Button
                variant="subtle"
                size="sm"
                radius="xl"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "60%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#9CA3AF",
                  border: "none",
                  color: "#FFFFFF",
                  width: "26px",
                  height: "26px",
                  minWidth: "26px",
                  padding: 0,
                  zIndex: 10,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handleNext}
              >
                <CaretRightIcon size={12} />
              </Button>
            </Box>
          )}

          {/* Progress Dots */}
          <Group gap="xs" mt="md">
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: activeDot === 1 ? theme.colors.primary[6] : "#e9ecef",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onClick={() => handleDotClick(1)}
            />
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: activeDot === 2 ? theme.colors.primary[6] : "#e9ecef",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onClick={() => handleDotClick(2)}
            />
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: activeDot === 3 ? theme.colors.primary[6] : "#e9ecef",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onClick={() => handleDotClick(3)}
            />
          </Group>
        </Stack>

        {/* Continue Button */}
        <Button
          fullWidth
          mt="md"
          rightSection={<CaretRightIcon size={16} />}
          onClick={handleContinue}
          style={{
            backgroundColor: "#124B8C", 
            color: "white",
            fontWeight: 600,
            fontSize: "16px",
            padding: "0px 22px", 
            height: "42px",
            borderRadius: "8px",
            border: "none",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#0f3d6b", 
              transform: "translateY(-1px)",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }
          }}
        >
          Continue
        </Button>
      </Card>
    </Stack>
    </Box>
  );
};

// Health Data Checking Component - Updated to show modal design
const HealthDataCheckingScreen: React.FC<{ onComplete: () => void; apiPromise?: Promise<any> }> = ({ onComplete, apiPromise }) => {
  const { colorScheme } = useMantineColorScheme();

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let transitionTimeoutId: ReturnType<typeof setTimeout>;
    let isMounted = true;

    // Wait for API call to complete, with a maximum timeout of 10 seconds
    timeoutId = setTimeout(() => {
      if (isMounted) {
        onComplete();
      }
    }, 10000);

    // If API promise is provided, wait for it to complete
    if (apiPromise) {
      apiPromise
        .then(() => {
          if (isMounted) {
            clearTimeout(timeoutId);
            // Transition after a short delay to show completion
            transitionTimeoutId = setTimeout(() => {
              if (isMounted) {
                onComplete();
              }
            }, 1000);
          }
        })
        .catch(() => {
          if (isMounted) {
            clearTimeout(timeoutId);
            // Still transition even on error after a delay
            transitionTimeoutId = setTimeout(() => {
              if (isMounted) {
                onComplete();
              }
            }, 2000);
          }
        });
    }

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      if (transitionTimeoutId) {
        clearTimeout(transitionTimeoutId);
      }
    };
  }, [onComplete, apiPromise]);

  return (
    <Box 
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: colorScheme === "dark" 
          ? "#1A1B1E" 
          : "linear-gradient(90deg, #FFF8F6 0%, #FAF8FB 50%, #F5F8FA 100%)",
        padding: "20px",
      }}
    >
      <Container size="sm">
        <Stack align="center" gap="xl">
          {/* Logo */}
          <Image
            src={
              colorScheme === "dark"
                ? "/assets/white_logo.png"
                : "/assets/logo_main.png"
            }
            w={180}
          />

          {/* Main Content Card */}
          <Card 
            withBorder 
            radius="lg" 
            p="xl" 
            style={{
              backgroundColor: colorScheme === "dark" ? "#25262b" : "white",
              boxShadow: colorScheme === "dark" 
                ? "0 4px 20px rgba(0, 0, 0, 0.3)" 
                : "0 4px 20px rgba(0, 0, 0, 0.08)",
              width: "100%",
              maxWidth: "500px",
              border: colorScheme === "dark" ? "1px solid #373A40" : undefined,
            }}
          >
            <Stack align="center" gap="xl">
              {/* Title */}
              <Text 
                size="xl"
                fw={600} 
                c={colorScheme === "dark" ? "#5B9FD8" : "#124B8C"}
                ta="center"
              >
                Checking Your Health Data...
              </Text>

              {/* Animated Loader Circle with Magnifying Glass */}
              <Box
                style={{
                  width: 80,
                  height: 80,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Solid base circle */}
                <Box
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    border: colorScheme === "dark" ? "3px solid #4A4D57" : "3px solid #E5E7EB",
                  }}
                />
                
                {/* Rotating blue border segment */}
                <Box
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    border: "3px solid transparent",
                    borderTopColor: colorScheme === "dark" ? "#5B9FD8" : "#124B8C",
                    borderRightColor: colorScheme === "dark" ? "#5B9FD8" : "#124B8C",
                    animation: "spin 2s linear infinite",
                  }}
                />
                
                {/* Static magnifying glass icon in center */}
                <MagnifyingGlass size={32} color="#FF6B6B" weight="regular" />
                
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </Box>

              {/* Status Text */}
              <Text 
                size="md"
                c={colorScheme === "dark" ? "gray.4" : "dark.5"}
                fw={500}
                ta="center"
              >
                Fetching your health records
              </Text>
            </Stack>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

const OnboardingScreen: React.FC<{setIsOnboarding: (isOnboarding: boolean) => void, onSkip: () => void}> = ({setIsOnboarding, onSkip}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [currentScreen, setCurrentScreen] = useState<'onboarding' | 'checking' | 'data-fetched'>('onboarding');
  const [userFirstName, setUserFirstName] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [healthDataPromise, setHealthDataPromise] = useState<Promise<any> | undefined>(undefined);

  // API hooks
  const { data: termsData, isLoading: termsLoading } = useTermsAccepted();
  const { data: currentUserData } = useMe();
  const { mutateAsync: getHealthData } = useCheckHealthData();
  const updateUserMutation = useUpdateMe({
    mutation: {
      onSuccess: () => {
        setShowTermsModal(false);
        // Continue with onboarding flow
      },
      onError: (error) => {
        console.error("Failed to update terms acceptance:", error);
      },
    },
  });

  useEffect(() => {
    // Get user data from localStorage
    const storedUser = localStorage.getItem("tanstack.auth.user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserFirstName(userData?.user?.first_name || "User");
        setUserId(userData?.user?.id || null);
      } catch (error) {
        console.error("Failed to parse stored user:", error);
        setUserFirstName("User");
      }
    }
  }, []);

  useEffect(() => {
    if (currentUserData?.id && !userId) {
      setUserId(currentUserData.id);
    }
  }, [currentUserData, userId]);

  // Check terms acceptance when data is loaded
  useEffect(() => {
    if (termsData && !termsLoading) {
      // If terms are not accepted, show the modal
      if (!termsData.terms_and_conditions_accepted) {
        setShowTermsModal(true);
      }
    }
  }, [termsData, termsLoading]);

  // Handle button click
  const handleCheckHealthData = () => {
    // Show the checking screen (with modal design)
    setCurrentScreen('checking');
    
    // Call the API to check health data and store the promise
    const healthDataCall = getHealthData()
      .then(() => {
        // On success, proceed to records screen
        // The HealthDataCheckingScreen will handle the transition timing
      })
      .catch((err) => {
        console.error("Failed to check health data:", err);
        // On error, still proceed to next screen after timeout
        // You can add error handling UI here if needed
        throw err; // Re-throw to let the component handle it
      });
    
    setHealthDataPromise(healthDataCall);
  };

  // Handle loading completion - go directly to data-fetched screen
  const handleLoadingComplete = () => {
    setCurrentScreen('data-fetched');
  };

  // Handle terms acceptance
  const handleAcceptTerms = () => {
    if (userId) {
      updateUserMutation.mutate({
        id: userId,
        data: {
          terms_and_conditions_accepted: true,
        },
      });
    } else {
      const storedUser = localStorage.getItem("tanstack.auth.user");
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const extractedUserId = userData?.user?.id;
          if (extractedUserId) {
            updateUserMutation.mutate({
              id: extractedUserId,
              data: {
                terms_and_conditions_accepted: true,
              },
            });
          }
        } catch (error) {
          console.error('Failed to parse user data:', error);
        }
      }
    }
  };

  // Handle terms modal close
  const handleCloseTermsModal = () => {
    setShowTermsModal(false);
  };

  // Handle onboarding completion - update is_onboarded status
  const handleOnboardingComplete = () => {
    const targetUserId = userId || currentUserData?.id;
    
    if (targetUserId) {
      updateUserMutation.mutate({
        id: targetUserId,
        data: {
          is_onboarded: true,
        },
      }, {
        onSuccess: () => {
          // After successfully updating is_onboarded, call setIsOnboarding
          setIsOnboarding(true);
        },
        onError: (error) => {
          console.error("Failed to update onboarding status:", error);
          // Still proceed even if update fails
          setIsOnboarding(true);
        },
      });
    } else {
      // If no userId available, still proceed
      setIsOnboarding(true);
    }
  };

  // Handle skip - update is_onboarded status and call onSkip
  const handleSkip = () => {
    const targetUserId = userId || currentUserData?.id;
    
    if (targetUserId) {
      updateUserMutation.mutate({
        id: targetUserId,
        data: {
          is_onboarded: true,
        },
      }, {
        onSuccess: () => {
          // After successfully updating is_onboarded, call onSkip
          onSkip();
        },
        onError: (error) => {
          console.error("Failed to update onboarding status:", error);
          // Still proceed even if update fails
          onSkip();
        },
      });
    } else {
      // If no userId available, still proceed
      onSkip();
    }
  };

  // Render based on current screen
  if (currentScreen === 'checking') {
    return <HealthDataCheckingScreen onComplete={handleLoadingComplete} apiPromise={healthDataPromise} />;
  }

  if (currentScreen === 'data-fetched') {
    return <DataFetchedScreen  setIsOnboarding={handleOnboardingComplete}/>;
  }

  // Mobile Layout Component
  const MobileLayout = () => (
    <Container size="sm" p="md" style={{ backgroundColor: "transparent" }}>
      <Stack align="center" gap="lg">
        {/* Logo Section */}
        <Box mt="sm">
          <Group justify="center" align="center">
            <Image
              src={
                colorScheme === "dark"
                  ? "/assets/white_logo.png"
                  : "/assets/logo.png"
              }
              w={140}
            />
          </Group>
        </Box>

        {/* Welcome Message */}
        <Stack align="center" gap="md">
          <Text 
            size="xl"
            fw={600} 
            c={colorScheme === "dark" ? "white" : "dark.7"} 
            ta="center"
          >
            Welcome, {userFirstName} to your Health Journey!
          </Text>

          {/* Health Icon */}
          <Box
            style={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: `radial-gradient(circle, rgba(139, 90, 155, 0.7) 0%, rgba(156, 39, 176, 0.6) 20%, rgba(233, 30, 99, 0.5) 40%, rgba(255, 182, 193, 0.4) 60%, rgba(255, 255, 255, 0.95) 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              padding: "8px",
            }}
          >
            <Box
              style={{
                width: 84,
                height: 84,
                borderRadius: "50%",
                background: `linear-gradient(135deg, #8B5A9B 0%, #E91E63 50%, #4A90E2 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
              }}
            >
              <PulseIcon size={32} color="white" weight="light" />
            </Box>
          </Box>
        </Stack>

        {/* Main Heading */}
        <Text 
          size="lg"
          fw={600} 
          c="primary.7" 
          ta="center"
        >
          Let's Start Building Your Personalized Care Path
        </Text>

        {/* Description */}
        <Text 
          size="sm"
          c={colorScheme === "dark" ? "gray.4" : "dark.5"} 
          ta="center" 
          lh={1.5}
        >
          Connect your health data to unlock personalized care OneSelf Path®, helpful insights, and smart
          recommendations—all tailored just for you.
        </Text>

        {/* Feature Cards - Mobile Stack */}
        <Stack gap="md" w="100%">
          {/* Personalized Insights */}
          <Card withBorder radius="md" p="md">
            <Group>
              <Box
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.colors.positive[1],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HandHeartIcon size={20} color={theme.colors.positive[7]} />
              </Box>
              <Stack gap={2} style={{ flex: 1 }}>
                <Text fw={600} size="sm" c={colorScheme === "dark" ? "white" : "dark.8"}>
                  Personalized Insights
                </Text>
                <Text size="xs" c={colorScheme === "dark" ? "gray.4" : "dark.5"} lh={1.4}>
                  Get tailored health recommendations based on your unique data
                </Text>
              </Stack>
            </Group>
          </Card>

          {/* Care Gap Analysis */}
          <Card withBorder radius="md" p="md">
            <Group>
              <Box
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.colors.primary[1],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PulseIcon size={20} color={theme.colors.primary[7]} />
              </Box>
              <Stack gap={2} style={{ flex: 1 }}>
                <Text fw={600} size="sm" c={colorScheme === "dark" ? "white" : "dark.8"}>
                  Care Gap Analysis
                </Text>
                <Text size="xs" c={colorScheme === "dark" ? "gray.4" : "dark.5"} lh={1.4}>
                  Identify missing screenings and preventive care opportunities
                </Text>
              </Stack>
            </Group>
          </Card>

          {/* Secure & Private */}
          <Card withBorder radius="md" p="md">
            <Group>
              <Box
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.colors.warning[1],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldCheckIcon size={20} color={theme.colors.warning[7]} />
              </Box>
              <Stack gap={2} style={{ flex: 1 }}>
                <Text fw={600} size="sm" c={colorScheme === "dark" ? "white" : "dark.8"}>
                  Secure & Private
                </Text>
                <Text size="xs" c={colorScheme === "dark" ? "gray.4" : "dark.5"} lh={1.4}>
                  Your data is protected with enterprise-grade security
                </Text>
              </Stack>
            </Group>
          </Card>
        </Stack>

        {/* CTA Buttons */}
        <Stack align="center" gap="xs" w="100%" mt="md">
          <Button
            size="md"
            radius="xl"
            fullWidth
            style={{
              background: "#124B8C",
              border: "none",
              fontSize: "14px",
              fontWeight: 600,
            }}
            leftSection={<PulseIcon size={16} />}
            onClick={handleCheckHealthData}
          >
            Check My Health Data
          </Button>
          
          <Button
            variant="subtle"
            size="sm"
            c={colorScheme === "dark" ? "gray.4" : "dark.5"}
            style={{
              backgroundColor: "transparent",
              fontSize: "12px",
              fontWeight: 400,
            }}
            onClick={handleSkip}
          >
            Skip
          </Button>
        </Stack>
      </Stack>
    </Container>
  );

  // Desktop Layout Component
  const DesktopLayout = () => (
    <Box 
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // background: "linear-gradient(90deg, #FFF8F6 0%, #FAF8FB 50%, #F5F8FA 100%)",
        padding: "20px",
      }}
    >
      <Card 
        withBorder 
        radius="lg" 
        p="xl" 
        style={{
          border: `2px solid ${theme.colors.primary[4]}`,
          backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "white",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          width: "100%",
          maxWidth: "900px",
        }}
      >
        <Container size="md" p={0}>
          <Stack align="center" gap="xl">
            {/* Logo Section */}
            <Box mt="md">
              <Group justify="center" align="center">
                <Image
                  src={
                    colorScheme === "dark"
                      ? "/assets/logo_White.png"
                      : "/assets/logo_main.png"
                  }
                  w={180}
                />
              </Group>
            </Box>

            {/* Welcome Message */}
            <Stack align="center" gap="md">
              <Text 
                size="28px"
                fw={600} 
                c={colorScheme === "dark" ? "white" : "dark.7"} 
                ta="center"
              >
             Welcome, {userFirstName} to your Health Journey!
              </Text>

              {/* Health Icon */}
              <Box
                style={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  background: "linear-gradient(180deg, #ffeafa 0%,rgba(114, 112, 108, 0.5) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0px 10px 20px rgba(0,0,0,0.15)",
                }}
              >
                <Box
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "50%",
                    background: "linear-gradient(180deg, #6b3a91 0%, #0b36b0 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0px 4px 10px rgba(0,0,0,0.12)",
                  }}
                >
                  <PulseIcon size={32} color="white" weight="light" />
                </Box>
              </Box>
            </Stack>

            {/* Main Heading */}
            <Text 
              size="22px"
              fw={600} 
              c="primary.7" 
              ta="center"
            >
              Let's Start Building Your Personalized Care Path
            </Text>

            {/* Description */}
            <Text 
              size="md"
              c={colorScheme === "dark" ? "gray.4" : "dark.5"} 
              ta="center" 
              maw={600} 
              lh={1.6}
            >
              Connect your health data to unlock personalized care OneSelf Path®, helpful insights, and smart
              recommendations—all tailored just for you.
            </Text>

            {/* Feature Cards - Desktop Grid */}
            <SimpleGrid cols={3} spacing="xl" w="100%">
              {/* Personalized Insights */}
              <Stack align="center" gap="md" maw={200}>
                <Box
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: "30%",
                    backgroundColor: theme.colors.primary[1],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <HandHeartIcon size={24} color={theme.colors.primary[7]} />
                </Box>
                <Text fw={600} size="lg" c={colorScheme === "dark" ? "white" : "dark.8"} ta="center">
                  Personalized Insights
                </Text>
                <Text size="sm" c={colorScheme === "dark" ? "gray.4" : "dark.5"} ta="center" lh={1.5}>
                  Get tailored health recommendations based on your unique data
                </Text>
              </Stack>

              {/* Care Gap Analysis */}
              <Stack align="center" gap="md" maw={200}>
                <Box
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: "30%",
                    backgroundColor: theme.colors.primary[1],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PulseIcon size={24} color={theme.colors.primary[7]} />
                </Box>
                <Text fw={600} size="lg" c={colorScheme === "dark" ? "white" : "dark.8"} ta="center">
                  Care Gap Analysis
                </Text>
                <Text size="sm" c={colorScheme === "dark" ? "gray.4" : "dark.5"} ta="center" lh={1.5}>
                  Identify missing screenings and preventive care opportunities
                </Text>
              </Stack>

              {/* Secure & Private */}
              <Stack align="center" gap="md" maw={200}>
                <Box
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: "30%",
                      backgroundColor: theme.colors.primary[1],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ShieldCheckIcon size={24} color={theme.colors.primary[7]} />
                </Box>
                <Text fw={600} size="lg" c={colorScheme === "dark" ? "white" : "dark.8"} ta="center">
                  Secure & Private
                </Text>
                <Text size="sm" c={colorScheme === "dark" ? "gray.4" : "dark.5"} ta="center" lh={1.5}>
                  Your data is protected with enterprise-grade security
                </Text>
              </Stack>
            </SimpleGrid>

            {/* CTA Buttons */}
            <Stack align="center" gap="xs" mt="lg">
              <Button
                size="lg"
                radius="md"
                w={600}
                px={40}
                py={12}
                style={{
                  background: `linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)`,
                  border: "none",
                  fontSize: "16px",
                  fontWeight: 600,
                  minWidth: "250px",
                }}
                leftSection={<PulseIcon size={18} />}
                onClick={handleCheckHealthData}
              >
                Check My Health Data
              </Button>
              
              <Button
                variant="subtle"
                size="sm"
                c={colorScheme === "dark" ? "gray.4" : "dark.5"}
                style={{
                  backgroundColor: "transparent",
                  fontSize: "14px",
                  fontWeight: 400,
                }}
                onClick={handleSkip}
              >
                Skip
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Card>
    </Box>
  );

  return (
    <Box>
      {/* Mobile Layout - shown on mobile only */}
      <Box hiddenFrom="md">
        <MobileLayout />
      </Box>

      {/* Desktop Layout - shown on desktop/tablet */}
      <Box visibleFrom="md">
        <DesktopLayout />
      </Box>

      {/* Terms and Conditions Modal */}
      <TermsConditionsModal
        opened={showTermsModal}
        onClose={handleCloseTermsModal}
        onAccept={handleAcceptTerms}
        loading={updateUserMutation.isPending}
      />
    </Box>
  );
};

export default OnboardingScreen;
